import { create_error_code_toast } from "@/components/shared/error-code-toast";
import { clientFetch, url } from "@/components/shared/fetch-client/fetch_client";
import FormField from "@/components/shared/form/form_field";
import ReadSessionWrapper from "@/components/shared/read-session-wrapper";
import { FormikWrapper } from "@/components/shared/wrappers/formik-wrapper";
import PersonPinOutlinedIcon from "@mui/icons-material/PersonPinOutlined";
import LoadingButton from "@mui/lab/LoadingButton";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { useRouter } from "next/router";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import * as Yup from "yup";
import DialogWrapper from "../../../shared/dialog/dialog-wrapper";
import * as validation from "../../../shared/validation";

export default function UserImpersonateDialog({ target_user, open, on_close }) {
	return (
		<ReadSessionWrapper>
			{(props) => (
				<UserImpersonateDialogContent
					target_user={target_user}
					open={open}
					on_close={on_close}
					{...props}
				/>
			)}
		</ReadSessionWrapper>
	);
}

function UserImpersonateDialogContent({
	target_user,
	open,
	on_close,
	user,
	update_session,
	callback_url,
}) {
	/* ---------------------------------- HOOKS --------------------------------- */
	const { t } = useTranslation();
	const router = useRouter();
	const [loading, setLoading] = useState(false);

	/* ------------------------------ FORMIK STUFF ------------------------------ */
	const formValues = {
		otp_code: "",
	};
	const formValidation = Yup.object().shape({
		otp_code: validation.inputOtp(t),
	});

	/* -------------------------------- NEXT AUTH ------------------------------- */

	/* --------------------------- DELETE USER HANDLER -------------------------- */
	const impersonateUserHandler = async (values, { setFieldError }) => {
		setLoading(true);
		setFieldError("otp_code", null);

		const { status, body } = await clientFetch(
			url`/api/back-office/user/${target_user.user_id}/impersonate`,
			{
				params: { otp_code: values.otp_code },
			},
		).post();

		setLoading(false);
		switch (status) {
			case 201: {
				// Update session
				await clientFetch(url`/api/auth/session`, {
					params: { access_token: body.access_token, impersonate: 1 },
				}).get();

				await update_session();
				on_close();

				// Redirect to the homepage if the current url is back-office url. Otherwise, redirect to the current url
				router.asPath.includes("back-office") ? router.push("/") : router.push(callback_url);

				break;
			}

			case 401:
				// When invalid 2FA
				if (body.detail === "Invalid OTP") {
					setFieldError(
						"otp_code",
						t("back_office.customers.user_management.dialog.impersonate.otp_invalid"),
					);
				}
				break;

			default:
				create_error_code_toast(t, body?.error_code);
				break;
		}
	};

	if (!target_user) {
		return null;
	}

	return (
		<DialogWrapper
			title={t("back_office.customers.user_management.dialog.impersonate.title")}
			open={open}
			on_close={on_close}
			close_button
		>
			<FormikWrapper
				initialValues={formValues}
				validationSchema={formValidation}
				onSubmit={impersonateUserHandler}
			>
				<Typography>
					<Trans
						i18nKey="back_office.customers.user_management.dialog.impersonate.description"
						values={{ username: target_user.username }}
					/>
				</Typography>

				{user?.otp_enabled ? (
					<>
						<Typography sx={{ mb: 0.5, mt: 1 }} fontWeight={500}>
							{t("inputs.otp.label")}
						</Typography>
						<FormField
							auto_focus
							name="otp_code"
							type="text"
							size="medium"
							label=""
							placeholder={t("inputs.otp.placeholder")}
						/>
					</>
				) : (
					<Alert variant="outlined" severity="info" sx={{ mt: 1 }}>
						{t("back_office.customers.user_management.dialog.impersonate.otp_not_enabled")}
					</Alert>
				)}

				<Divider sx={{ mb: 1, mt: 2 }} />

				<DialogActions sx={{ mb: 1, px: 0 }}>
					<Button variant="outlined" type="button" onClick={on_close} fullWidth>
						{t("buttons.cancel")}
					</Button>

					<LoadingButton
						variant="outlined"
						type="submit"
						disabled={!user.otp_enabled}
						loading={loading}
						startIcon={<PersonPinOutlinedIcon />}
						disableElevation
						fullWidth
					>
						{t("back_office.customers.user_management.table.body.buttons.impersonate")}
					</LoadingButton>
				</DialogActions>
			</FormikWrapper>
		</DialogWrapper>
	);
}
